/**
 * The shapes a picture of the graph is made of: plain data, in screen pixels (CSS pixels, the
 * scene's units). `picture.ts` decides what to draw; a painter draws it: `paint2d.ts` with
 * Canvas 2D (the processor), or `gl/painter.ts` with WebGL (the graphics card). Both read the
 * same list, so the two pictures match.
 *
 * Every shape is painted over the ones before it, in list order.
 */

/** How a shape is coloured. Colours are CSS colours (`#rrggbb`, `rgb(...)`...). */
export interface Paint {
  /** The inside's colour (`null`: not filled). */
  fill: string | null;
  /** The outline's colour (`null`: no outline). */
  stroke: string | null;
  /** The outline's width, centred on the shape's edge. */
  width: number;
  /** The opacity of the whole shape, from 0 to 1. */
  alpha: number;
  /** A dashed outline: drawn length, then gap (`null`: solid). */
  dash: readonly [number, number] | null;
}

/** A disc or a ring around (`x`, `y`). */
export interface Circle {
  type: "circle";
  x: number;
  y: number;
  r: number;
  paint: Paint;
}

/** A rectangle centred on (`x`, `y`), half sizes `hw` × `hh`, round corners, turned by `angle` radians. */
export interface Box {
  type: "box";
  x: number;
  y: number;
  hw: number;
  hh: number;
  radius: number;
  angle: number;
  paint: Paint;
}

/** A hexagon with a corner at the top and one at the bottom, its corners `r` from the centre. */
export interface Hexagon {
  type: "hexagon";
  x: number;
  y: number;
  r: number;
  paint: Paint;
}

/** An arrow head: a triangle with its tip at (`x`, `y`), pointing along (`ux`, `uy`). */
export interface Triangle {
  type: "triangle";
  x: number;
  y: number;
  ux: number;
  uy: number;
  /** From the tip to the base. */
  length: number;
  /** Half the base's width. */
  spread: number;
  paint: Paint;
}

/** A straight line; its colour, width and dashes are the paint's outline. */
export interface Segment {
  type: "segment";
  x1: number;
  y1: number;
  x2: number;
  y2: number;
  /** Round ends instead of square ones. */
  round: boolean;
  paint: Paint;
}

/** An open arrow head (two strokes meeting at the tip), as {@link Triangle} without its base. */
export interface Chevron {
  type: "chevron";
  x: number;
  y: number;
  ux: number;
  uy: number;
  length: number;
  spread: number;
  paint: Paint;
}

/** One line of text, its anchor at (`x`, `y`). Painted on Canvas 2D in every case. */
export interface Text {
  type: "text";
  text: string;
  x: number;
  y: number;
  /** A CSS font (`11px Inter`). */
  font: string;
  align: CanvasTextAlign;
  baseline: CanvasTextBaseline;
  /** Its colour. */
  color: string;
  /** A halo around the letters, in the background colour, so text stays readable over lines. */
  halo: { color: string; width: number } | null;
  alpha: number;
}

/**
 * A picture cut to a disc of radius `r` around (`x`, `y`), filling it (its middle kept when its
 * shape differs). `key` names it for the painter's {@link ImageLookup} (a node's attached file).
 * Painted on Canvas 2D in every case; nothing is drawn until the picture has loaded.
 */
export interface ImageShape {
  type: "image";
  key: string;
  x: number;
  y: number;
  r: number;
  alpha: number;
}

/** The loaded picture named `key`, if it has loaded. */
export type ImageLookup = (key: string) => CanvasImageSource | null;

/** Anything a picture holds. */
export type Shape = Circle | Box | Hexagon | Triangle | Segment | Chevron | Text | ImageShape;

/** The shapes without text nor pictures: what the graphics card can draw. */
export type FlatShape = Exclude<Shape, Text | ImageShape>;

/**
 * A picture, in two layers. `base` (groups, links, bundles, nodes: the many shapes) never holds
 * text, so a painter on the graphics card can draw it alone. `top` (the nodes' pictures, names,
 * counts, handles, previews: few shapes, often text) goes over it, always on Canvas 2D.
 */
export interface Picture {
  base: FlatShape[];
  top: Shape[];
}

/** A paint that only fills. */
export function filled(color: string, alpha = 1): Paint {
  return { fill: color, stroke: null, width: 0, alpha, dash: null };
}

/** A paint that only draws an outline (or a line). */
export function stroked(color: string, width: number, alpha = 1, dash: readonly [number, number] | null = null): Paint {
  return { fill: null, stroke: color, width, alpha, dash };
}

/** A paint that fills and outlines. */
export function both(fill: string, stroke: string, width: number, alpha = 1): Paint {
  return { fill, stroke, width, alpha, dash: null };
}
